import * as Tone from 'tone';

type Mood = 'calm' | 'upbeat' | 'tense' | 'triumph' | 'chill';

interface MoodConfig {
  key: string[];       // Scale notes to pick from
  bassNotes: string[]; // Bass chord roots (4 chords, cycled)
  bpm: number;
  density: number;     // 0-1, proportion of notes vs rests in melody
}

const MOODS: Record<Mood, MoodConfig> = {
  calm: {
    key: ['C4', 'D4', 'E4', 'G4', 'A4', 'C5', 'D5', 'E5', 'G5'],
    bassNotes: ['C2', 'F2', 'G2', 'C2'],
    bpm: 95,
    density: 0.5,
  },
  upbeat: {
    key: ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'C5', 'D5', 'E5', 'G5'],
    bassNotes: ['C2', 'F2', 'G2', 'E2'],
    bpm: 125,
    density: 0.7,
  },
  tense: {
    key: ['C4', 'Eb4', 'F4', 'G4', 'Bb4', 'C5', 'Eb5', 'F5', 'G5'],
    bassNotes: ['C2', 'Ab2', 'Bb2', 'G2'],
    bpm: 108,
    density: 0.55,
  },
  triumph: {
    key: ['C4', 'E4', 'F4', 'G4', 'A4', 'B4', 'C5', 'E5', 'G5', 'A5'],
    bassNotes: ['C2', 'G2', 'A2', 'F2'],
    bpm: 138,
    density: 0.8,
  },
  chill: {
    key: ['D4', 'E4', 'F#4', 'A4', 'B4', 'D5', 'E5', 'F#5'],
    bassNotes: ['D2', 'G2', 'A2', 'D2'],
    bpm: 82,
    density: 0.4,
  },
};

/**
 * Procedural chiptune music with variation.
 *
 * Instead of fixed loops, generates melodies using constrained random walks
 * on the scale. Notes vary on each loop repeat. Channels occasionally mute
 * for dynamic arrangement. Result: music that follows the same harmonic
 * structure but never sounds identical twice.
 */
export class MusicEngine {
  private lead: Tone.Synth | null = null;
  private bass: Tone.Synth | null = null;
  private arp: Tone.Synth | null = null;

  private melodySeq: Tone.Sequence | null = null;
  private bassSeq: Tone.Sequence | null = null;
  private arpSeq: Tone.Sequence | null = null;

  private initialized: boolean = false;
  private playing: boolean = false;
  private currentMood: Mood = 'calm';

  private sectionCounter: number = 0;
  private tickCounter: number = 0;
  private leadMuted: boolean = false;

  async init(): Promise<void> {
    if (this.initialized) return;
    await Tone.start();

    this.lead = new Tone.Synth({
      oscillator: { type: 'square' },
      envelope: { attack: 0.01, decay: 0.15, sustain: 0.2, release: 0.1 },
    }).toDestination();
    this.lead.volume.value = -12;

    this.bass = new Tone.Synth({
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.01, decay: 0.3, sustain: 0.5, release: 0.1 },
    }).toDestination();
    this.bass.volume.value = -8;

    this.arp = new Tone.Synth({
      oscillator: { type: 'square' },
      envelope: { attack: 0.005, decay: 0.06, sustain: 0.08, release: 0.04 },
    }).toDestination();
    this.arp.volume.value = -18;

    this.initialized = true;
  }

  setVolume(vol: number): void {
    const clamped = Math.max(0, Math.min(1, vol));
    const db = clamped > 0 ? Tone.gainToDb(clamped * 0.5) : -Infinity;
    if (this.lead) this.lead.volume.value = db - 4;
    if (this.bass) this.bass.volume.value = db;
    if (this.arp) this.arp.volume.value = db - 10;
  }

  play(mood: Mood = 'calm'): void {
    if (!this.initialized) return;
    if (this.playing && mood === this.currentMood) return;

    this.stopSequences();
    this.currentMood = mood;
    this.playing = true;
    this.sectionCounter = 0;
    this.tickCounter = 0;
    this.leadMuted = false;

    const config = MOODS[mood];

    try {
      Tone.getTransport().bpm.value = config.bpm;

      this.startMelody(config);
      this.startBass(config);
      this.startArp(config);

      // Always restart transport to ensure clean state
      if (Tone.getTransport().state === 'started') {
        Tone.getTransport().stop();
      }
      Tone.getTransport().start();
    } catch {
      // Tone.js can throw on rapid stop/start — recover gracefully
      this.playing = false;
    }
  }

  private startMelody(config: MoodConfig): void {
    const melody = this.generateMelody(config, 32);

    this.melodySeq = new Tone.Sequence((time, note) => {
      this.tickCounter++;

      // Every 32 beats, regenerate melody with variation
      if (this.tickCounter % 32 === 0) {
        this.sectionCounter++;
        const newMelody = this.generateMelody(MOODS[this.currentMood], 32);
        const isB = this.sectionCounter % 2 === 1;

        for (let i = 0; i < melody.length; i++) {
          if (isB) {
            // B section: use new melody
            melody[i] = newMelody[i]!;
          } else {
            // A section: mix — keep some old, swap some new
            if (i % 3 === 0) melody[i] = newMelody[i]!;
          }
        }

        // Every 4th section: mute lead for a "bridge" feel (bass + arp only)
        if (this.sectionCounter % 4 === 3) {
          this.leadMuted = true;
          const beatDuration = (60 / MOODS[this.currentMood].bpm) * 1000;
          setTimeout(() => { this.leadMuted = false; }, beatDuration * 8);
        }
      }

      if (note && this.lead && !this.leadMuted) {
        this.lead.triggerAttackRelease(note, '8n', time);
      }
    }, melody, '8n');
    this.melodySeq.loop = true;
    this.melodySeq.start(0);
  }

  private startBass(config: MoodConfig): void {
    // Bass: chord roots, 8 beats per chord (half note feel)
    const bassPattern: (string | null)[] = [];
    for (const root of config.bassNotes) {
      bassPattern.push(root, null, null, null, root, null, null, null);
    }

    this.bassSeq = new Tone.Sequence((time, note) => {
      if (note && this.bass) {
        this.bass.triggerAttackRelease(note, '4n', time);
      }
    }, bassPattern, '8n');
    this.bassSeq.loop = true;
    this.bassSeq.start(0);
  }

  private startArp(config: MoodConfig): void {
    // Arpeggio: cycles through scale tones in groups, shifted per bar
    const arpNotes: (string | null)[] = [];
    const scale = config.key;

    for (let bar = 0; bar < 4; bar++) {
      const rootIdx = (bar * 2) % scale.length;
      for (let i = 0; i < 8; i++) {
        const idx = (rootIdx + (i % 4)) % scale.length;
        // Add occasional rests for rhythm
        if (i === 3 || i === 7) {
          arpNotes.push(null);
        } else {
          arpNotes.push(scale[idx]!);
        }
      }
    }

    this.arpSeq = new Tone.Sequence((time, note) => {
      if (note && this.arp) {
        this.arp.triggerAttackRelease(note, '16n', time);
      }
    }, arpNotes, '16n');
    this.arpSeq.loop = true;
    this.arpSeq.start(0);
  }

  /**
   * Generate a melody via constrained random walk on the scale.
   * Each call produces a different melody due to the section counter.
   */
  private generateMelody(config: MoodConfig, length: number): (string | null)[] {
    const scale = config.key;
    const melody: (string | null)[] = [];
    let pos = Math.floor(scale.length / 2);
    const seed = this.sectionCounter * 17 + 7;

    for (let i = 0; i < length; i++) {
      // Hash for pseudo-random decisions
      const h1 = ((seed + i * 31) * 2654435761) >>> 0;
      const h2 = ((seed + i * 47 + 13) * 2246822519) >>> 0;
      const r1 = (h1 % 1000) / 1000;
      const r2 = (h2 % 1000) / 1000;

      // Rest probability
      if (r1 > config.density) {
        melody.push(null);
        continue;
      }

      // Step: mostly small (-1 to +1), occasionally bigger
      let step: number;
      if (r2 < 0.2) step = -2;
      else if (r2 < 0.4) step = -1;
      else if (r2 < 0.6) step = 0; // Repeat same note
      else if (r2 < 0.8) step = 1;
      else step = 2;

      pos = Math.max(0, Math.min(scale.length - 1, pos + step));

      // Strong beats: bias toward chord tones (even indices in scale)
      if (i % 4 === 0) {
        pos = Math.round(pos / 2) * 2;
        pos = Math.min(pos, scale.length - 1);
      }

      // Phrase ending: resolve toward root
      if (i % 8 === 7) {
        pos = pos > scale.length / 2 ? pos - 1 : pos + 1;
        pos = Math.max(0, Math.min(scale.length - 1, pos));
      }

      melody.push(scale[pos]!);
    }

    return melody;
  }

  setMood(mood: Mood): void {
    if (mood === this.currentMood && this.playing) return;
    this.play(mood);
  }

  /** Ensure music is playing — call periodically to recover from silent failures. */
  ensurePlaying(): void {
    if (!this.initialized || !this.playing) return;
    if (Tone.getTransport().state !== 'started') {
      // Transport died — restart
      this.play(this.currentMood);
    }
  }

  stop(): void {
    if (!this.playing) return;
    this.playing = false;
    this.stopSequences();
    Tone.getTransport().stop();
  }

  private stopSequences(): void {
    this.melodySeq?.stop();
    this.melodySeq?.dispose();
    this.melodySeq = null;
    this.bassSeq?.stop();
    this.bassSeq?.dispose();
    this.bassSeq = null;
    this.arpSeq?.stop();
    this.arpSeq?.dispose();
    this.arpSeq = null;
  }

  get isPlaying(): boolean { return this.playing; }
  get mood(): Mood { return this.currentMood; }

  destroy(): void {
    this.stop();
    this.lead?.dispose();
    this.bass?.dispose();
    this.arp?.dispose();
    this.lead = null;
    this.bass = null;
    this.arp = null;
    this.initialized = false;
  }
}
